import { Control, FieldValues, Path, useController } from "react-hook-form";
import { Input } from "..";
import { useFieldId, useFieldRequired } from "./FieldWrapper/FieldWrapper";

type FormInputProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  error?: string | null;
  valueAsNumber?: boolean;
} & Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "error">;

export function FormInput<T extends FieldValues>({
  name,
  control,
  error,
  valueAsNumber,
  ...rest
}: FormInputProps<T>) {
  const { field, fieldState } = useController({ name, control });
  // Set by the enclosing `FieldWrapper`, whose `<label>` points here. An explicit `id` still wins.
  const fieldId = useFieldId();
  const isRequired = useFieldRequired();

  const isNumeric = valueAsNumber || rest.type === "number";
  const handleChange = isNumeric
    ? (val: string) => field.onChange(val === "" ? "" : Number(val))
    : field.onChange;

  return (
    <Input
      {...rest}
      id={rest.id ?? fieldId}
      aria-required={isRequired || undefined}
      // Gets the numeric keypad on a phone. `type="number"` alone does not on every browser, and
      // the durations are the fields a technician types on one.
      inputMode={rest.inputMode ?? (isNumeric ? "numeric" : undefined)}
      value={field.value ?? ""}
      onChange={handleChange}
      ref={field.ref}
      error={error ?? fieldState.error?.message ?? null}
    />
  );
}
